import {
  GAMEPLAY_WAVE_STYLES,
  isGameplayWaveStyle,
  type GameplayWaveStyle,
} from './GameplayWave.js';
import { DEFAULT_PALETTE_ID, isPaletteId } from '../palette/VisualPalette.js';
import {
  DEFAULT_GAMEPLAY_WAVE_SETTINGS,
  isGameplayWaveMode,
  type GameplayWaveSettings,
} from './GameplayWaveDirector.js';

/**
 * The `gameplayWave` section of a visual recipe.
 *
 * Its own section rather than folded into the existing wave settings, because
 * it answers a different question. `waveMode` and `waveStyles` decide what the
 * *artwork* emits; these decide how the thing the player taps is drawn. Sharing
 * one field would mean a background of smoke forced smoke waves at the player,
 * and §23 asks for exactly the opposite.
 *
 * ## What an absent section means
 *
 * MANDALA, fixed — the game as it was before any of this existed. That is the
 * single most important property in this file. Every recipe saved so far lacks
 * the section, and a load that quietly turned those into randomised styles would
 * change how somebody's saved artwork plays without them touching anything.
 * `parseGameplayWave` therefore treats absent, malformed and empty identically,
 * and never invents variety it was not asked for.
 */

export interface GameplayWaveRecipeSection {
  readonly mode: string;
  readonly defaultStyle: string;
  readonly styles?: Readonly<Record<string, number>>;
  /**
   * Which palette the pigment styles use.
   *
   * Optional, and its absence means the default — so a recipe saved before
   * gameplay waves had colour keeps loading, and simply gains pigment where it
   * used to have hardcoded blue-and-mauve.
   */
  readonly palette?: string;
  readonly seed?: number;
}

/**
 * Reads the section, or returns the default.
 *
 * Tolerant on the way in and strict on the way out: anything unrecognised is
 * dropped rather than rejected, because a recipe written by a later build
 * should still open in this one and play as MANDALA rather than fail to load.
 */
export function parseGameplayWave(input: unknown): {
  readonly settings: GameplayWaveSettings;
  readonly problems: readonly string[];
} {
  const problems: string[] = [];
  if (input === undefined || input === null) {
    return { settings: DEFAULT_GAMEPLAY_WAVE_SETTINGS, problems };
  }
  if (typeof input !== 'object') {
    return { settings: DEFAULT_GAMEPLAY_WAVE_SETTINGS, problems: ['gameplayWave is not an object'] };
  }

  const raw = input as Record<string, unknown>;

  const modeRaw = typeof raw.mode === 'string' ? raw.mode : '';
  const mode = isGameplayWaveMode(modeRaw) ? modeRaw : DEFAULT_GAMEPLAY_WAVE_SETTINGS.mode;
  if (modeRaw.length > 0 && !isGameplayWaveMode(modeRaw)) {
    problems.push(`unknown gameplay wave mode "${modeRaw}"; using ${mode}`);
  }

  const styleRaw = typeof raw.defaultStyle === 'string' ? raw.defaultStyle : '';
  const defaultStyle: GameplayWaveStyle = isGameplayWaveStyle(styleRaw)
    ? styleRaw
    : DEFAULT_GAMEPLAY_WAVE_SETTINGS.defaultStyle;
  if (styleRaw.length > 0 && !isGameplayWaveStyle(styleRaw)) {
    problems.push(`unknown gameplay wave style "${styleRaw}"; using ${defaultStyle}`);
  }

  const styles: Partial<Record<GameplayWaveStyle, number>> = {};
  const stylesRaw = raw.styles;
  if (typeof stylesRaw === 'object' && stylesRaw !== null) {
    for (const [key, value] of Object.entries(stylesRaw as Record<string, unknown>)) {
      if (!isGameplayWaveStyle(key)) {
        problems.push(`unknown gameplay wave style "${key}" in weights; ignored`);
        continue;
      }
      const weight = typeof value === 'number' && Number.isFinite(value) ? Math.max(0, value) : 0;
      if (weight > 0) styles[key] = weight;
    }
  }

  const paletteRaw = typeof raw.palette === 'string' ? raw.palette : '';
  const palette = isPaletteId(paletteRaw) ? paletteRaw : DEFAULT_PALETTE_ID;
  if (paletteRaw.length > 0 && !isPaletteId(paletteRaw)) {
    problems.push(`unknown gameplay wave palette "${paletteRaw}"; using ${palette}`);
  }

  const seed = typeof raw.seed === 'number' && Number.isFinite(raw.seed)
    ? raw.seed | 0
    : DEFAULT_GAMEPLAY_WAVE_SETTINGS.seed;

  return { settings: { mode, defaultStyle, styles, palette, seed }, problems };
}

/**
 * Writes the section, or nothing at all.
 *
 * Returns `undefined` for settings that are the default, so a recipe that never
 * asked about gameplay styles does not grow a section describing the absence of
 * a choice. That keeps saved files diffable and keeps "old recipe" and "new
 * recipe that happens to be MANDALA" byte-identical.
 */
export function serialiseGameplayWave(
  settings: GameplayWaveSettings,
): GameplayWaveRecipeSection | undefined {
  const isDefault =
    settings.mode === DEFAULT_GAMEPLAY_WAVE_SETTINGS.mode &&
    settings.defaultStyle === DEFAULT_GAMEPLAY_WAVE_SETTINGS.defaultStyle &&
    settings.seed === DEFAULT_GAMEPLAY_WAVE_SETTINGS.seed &&
    settings.palette === DEFAULT_GAMEPLAY_WAVE_SETTINGS.palette &&
    Object.keys(settings.styles).length === 0;
  if (isDefault) return undefined;

  const styles: Record<string, number> = {};
  for (const style of GAMEPLAY_WAVE_STYLES) {
    const weight = settings.styles[style];
    if (weight !== undefined && weight > 0) styles[style] = weight;
  }

  return {
    mode: settings.mode,
    defaultStyle: settings.defaultStyle,
    ...(Object.keys(styles).length > 0 ? { styles } : {}),
    // Written only when it is not the default, so a recipe that never chose a
    // palette does not grow the field on its next save.
    ...(settings.palette === DEFAULT_GAMEPLAY_WAVE_SETTINGS.palette
      ? {}
      : { palette: settings.palette }),
    seed: settings.seed,
  };
}

/** A weight table people will recognise, for the panel's WEIGHTED button. */
export const SUGGESTED_GAMEPLAY_WEIGHTS: Readonly<Partial<Record<GameplayWaveStyle, number>>> = {
  RIPPLE: 30,
  SMOKE: 20,
  CODE: 15,
  MATH: 10,
  SUMI: 10,
  WATERCOLOR: 10,
  INK_PAINT: 5,
};
